import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { CalendarBlank, Snowflake, SunDim, Drop, Bell, CaretDown, CaretUp } from '@phosphor-icons/react';
import { API_URL } from '../config/api';

const SPRAY_STATUS = {
  good: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300', label: 'Good', dot: 'bg-green-500' },
  marginal: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300', label: 'Caution', dot: 'bg-amber-500' },
  hold: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300', label: 'Hold', dot: 'bg-red-500' },
  unknown: { bg: 'bg-muted', text: 'text-muted-foreground', label: '?', dot: 'bg-muted-foreground' },
};

const UV_COLORS = {
  low: 'bg-green-400', moderate: 'bg-yellow-400', high: 'bg-orange-400', very_high: 'bg-red-400', extreme: 'bg-purple-500',
};

export const SprayPlanner = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const [showHumidity, setShowHumidity] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API_URL}/api/weather/spray-planner`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 400) {
        setError('location');
      } else {
        setError('fetch');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (error === 'location' || error === 'fetch') return null;
  if (loading) {
    return (
      <div data-testid="spray-planner-loading" className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="animate-pulse"><CardContent className="pt-4 pb-3 h-20" /></Card>
        ))}
      </div>
    );
  }
  if (!data) return null;

  const { planner, frost_alerts: frost, uv_forecast: uv, humidity_detail: humidity, notifications } = data;

  return (
    <div data-testid="spray-planner-section">
      <div className="flex items-center gap-2 mb-3">
        <CalendarBlank size={18} weight="duotone" className="text-primary" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">5-Day Spray Planner</h2>
      </div>

      {/* Smart Notifications */}
      {notifications && notifications.length > 0 && (
        <div className="mb-3 space-y-2" data-testid="smart-notifications">
          {notifications.map((n, i) => (
            <div key={i} className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${
              n.priority === 'critical' ? 'bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-200 border border-red-300 dark:border-red-800' :
              n.priority === 'high' ? 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 border border-amber-300 dark:border-amber-800' :
              'bg-blue-50 dark:bg-blue-950/30 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800'
            }`}>
              <Bell size={14} weight="fill" className="shrink-0 mt-0.5" />
              <span>{n.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* 5-Day Planner Grid */}
      <div className="space-y-2 mb-4">
        {planner.map((day, i) => {
          const style = SPRAY_STATUS[day.status] || SPRAY_STATUS.unknown;
          const isExpanded = expandedDay === i;
          return (
            <Card key={day.date} data-testid={`planner-day-${i}`}
              className={`cursor-pointer transition-all hover:shadow-sm ${day.is_today ? 'ring-2 ring-primary/30' : ''}`}
              onClick={() => setExpandedDay(isExpanded ? null : i)}>
              <CardContent className="py-3 px-4">
                <div className="flex items-center gap-3">
                  {/* Status dot */}
                  <div className={`w-3 h-3 rounded-full shrink-0 ${style.dot}`} />
                  {/* Day + date */}
                  <div className="min-w-[90px]">
                    <p className="text-sm font-semibold">{day.day}{day.is_today ? <span className="ml-1.5 text-[10px] font-medium text-primary">(Today)</span> : ''}</p>
                    <p className="text-[10px] text-muted-foreground">{day.day_full}</p>
                  </div>
                  {/* Temp */}
                  <div className="text-center min-w-[55px]">
                    <p className="text-sm font-medium">{day.high_f}°<span className="text-muted-foreground text-xs">/{day.low_f}°</span></p>
                  </div>
                  {/* Status badge */}
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${style.bg} ${style.text}`}>
                    {style.label}
                  </span>
                  {/* Best window */}
                  <div className="flex-1 text-right hidden sm:block">
                    {day.best_window ? (
                      <span className="text-xs text-muted-foreground">Best: <strong className="text-foreground">{day.best_window.label}</strong></span>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">No good window</span>
                    )}
                  </div>
                  {/* Quick stats */}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {day.rain_in > 0 && <span className="flex items-center gap-0.5"><Drop size={12} />{day.rain_in}"</span>}
                    <span className="flex items-center gap-0.5"><span className={day.wind_max_mph >= 12 ? 'text-red-500 font-semibold' : ''}>{day.wind_max_mph}mph</span></span>
                  </div>
                  {/* Expand arrow */}
                  {isExpanded ? <CaretUp size={14} className="text-muted-foreground" /> : <CaretDown size={14} className="text-muted-foreground" />}
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-border/50 grid grid-cols-3 gap-3 text-xs">
                    <div>
                      <p className="text-muted-foreground">Good hours</p>
                      <p className="font-semibold text-green-600 dark:text-green-400">{day.good_hours}h</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Marginal</p>
                      <p className="font-semibold text-amber-600 dark:text-amber-400">{day.marginal_hours}h</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Hold</p>
                      <p className="font-semibold text-red-600 dark:text-red-400">{day.hold_hours}h</p>
                    </div>
                    {day.reasons.length > 0 && (
                      <div className="col-span-3">
                        <p className="text-muted-foreground">Blockers: <span className="text-foreground">{day.reasons.join(', ')}</span></p>
                      </div>
                    )}
                    {day.best_window && (
                      <div className="col-span-3 sm:hidden">
                        <p className="text-muted-foreground">Best window: <strong className="text-foreground">{day.best_window.label}</strong></p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Bottom row: Frost + UV + Humidity */}
      <div className="grid md:grid-cols-3 gap-4">

        {/* Frost Alerts */}
        <Card data-testid="frost-alert-card">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-2">
              <Snowflake size={18} weight="duotone" className="text-blue-400" />
              <span className="font-semibold text-xs">Frost Watch</span>
            </div>
            {frost.length > 0 ? (
              <div className="space-y-1.5">
                {frost.map((a, i) => (
                  <div key={i} className={`px-2 py-1.5 rounded text-xs ${
                    a.severity === 'freeze' ? 'bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-300' :
                    a.severity === 'frost' ? 'bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300' :
                    'bg-cyan-50 dark:bg-cyan-950/30 text-cyan-700 dark:text-cyan-300'
                  }`}>
                    <p className="font-medium">{a.message}</p>
                    <p className="text-[10px] opacity-70">{a.day}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-green-600 dark:text-green-400 font-medium">No frost expected</p>
            )}
          </CardContent>
        </Card>

        {/* UV Index */}
        <Card data-testid="uv-index-card">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-2">
              <SunDim size={18} weight="duotone" className="text-amber-500" />
              <span className="font-semibold text-xs">UV Index</span>
            </div>
            <div className="flex items-end gap-1 h-16 mb-1.5">
              {uv.slice(0, 5).map((d, i) => {
                const maxUV = Math.max(...uv.slice(0, 5).map(x => x.uv_max), 1);
                const height = Math.max((d.uv_max / maxUV) * 100, 10);
                const color = UV_COLORS[d.level] || 'bg-gray-400';
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                    <span className="text-[9px] font-medium">{d.uv_max}</span>
                    <div className={`w-full rounded-sm ${color}`} style={{ height: `${height}%` }} title={d.advice} />
                    <span className="text-[8px] text-muted-foreground">{d.day}</span>
                  </div>
                );
              })}
            </div>
            {uv[0] && <p className="text-[10px] text-muted-foreground">{uv[0].advice}</p>}
          </CardContent>
        </Card>

        {/* Humidity & Dew Point */}
        <Card data-testid="humidity-detail-card" className="cursor-pointer" onClick={() => setShowHumidity(!showHumidity)}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Drop size={18} weight="duotone" className="text-teal-500" />
                <span className="font-semibold text-xs">Humidity & Dew Point</span>
              </div>
              {showHumidity ? <CaretUp size={12} className="text-muted-foreground" /> : <CaretDown size={12} className="text-muted-foreground" />}
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-lg font-bold">{humidity.current.humidity}%</p>
                <p className="text-[9px] text-muted-foreground">Now</p>
              </div>
              <div>
                <p className="text-lg font-bold">{humidity.current.dew_point_f}°</p>
                <p className="text-[9px] text-muted-foreground">Dew Point</p>
              </div>
              <div>
                <p className="text-lg font-bold">{humidity.max_humidity_24h}%</p>
                <p className="text-[9px] text-muted-foreground">24h Max</p>
              </div>
            </div>
            {/* Expanded 24h humidity trend */}
            {showHumidity && humidity.trend.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border/50">
                <div className="flex items-end gap-px h-12">
                  {humidity.trend.map((pt, i) => {
                    const height = Math.max(pt.humidity, 5);
                    const color = pt.humidity >= 85 ? 'bg-teal-500' : pt.humidity >= 70 ? 'bg-teal-400' : 'bg-teal-300';
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center">
                        <div className={`w-full rounded-sm ${color}`} style={{ height: `${height}%` }} title={`${pt.hour}: ${pt.humidity}% humidity, dew ${pt.dew_point_f}°F`} />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-0.5">
                  <span className="text-[8px] text-muted-foreground">{humidity.trend[0]?.hour}</span>
                  <span className="text-[8px] text-muted-foreground">{humidity.trend[humidity.trend.length - 1]?.hour}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
};
